/**
 * Google Analytics 4, and the browser tab's title.
 *
 * ── What was wrong ──
 *
 * gtag.js was a <script> in index.html with a hard-coded measurement id, so it
 * loaded on every page of every build — `npm run dev`, the layout harness,
 * preview deploys, a buyer's quote, and the registration form builders embed
 * on their own marketing sites. Page views were sent with the raw path, which
 * carries project ids, quote tokens and the public roadmap token; the raw user
 * id was set as `user_id`; and nothing turned off IP collection, Google
 * Signals or ad personalisation.
 *
 * ── What it does now ──
 *
 *  · Nothing at all unless VITE_GA_MEASUREMENT_ID is configured — a build
 *    without one is a complete no-op, and `window.gtag` stays undefined, so
 *    every existing `window.gtag?.(…)` call site no-ops with it.
 *  · Never on the buyer-facing pages (a quote, the embedded registration
 *    form): those readers are a builder's customers, not ours.
 *  · The tag is FETCHED after the app has started — after `load`, in idle
 *    time — so it never competes with first paint. Commands queue in
 *    `dataLayer` until it arrives, which is how gtag is designed to work.
 *  · IP anonymised; Google Signals and ad personalisation off.
 *  · `user_id` is a one-way SHA-256 of the email, never the email or the
 *    database id.
 *  · Every path is sent with its ids replaced by the route parameter
 *    (`/projects/:projectId/contacts`) and with no query string.
 *
 * The TAB TITLE is here too, because it is the same question — "what is this
 * page called?" — and because it must be set whether or not analytics is on:
 * it is what a person reads in their tab bar and their history. Every page is
 * "<Page> · SaleFish".
 */
import { useEffect, useLayoutEffect } from "react";
import { msg, type Msg } from "@shared/i18n";

const PRODUCT = "SaleFish";

/** A GA4 measurement id, and nothing else — it is interpolated into a script URL. */
const MEASUREMENT_ID_SHAPE = /^G-[A-Z0-9]{4,20}$/;

function configuredId(): string | null {
  const raw = (import.meta.env.VITE_GA_MEASUREMENT_ID as string | undefined)?.trim();
  return raw && MEASUREMENT_ID_SHAPE.test(raw) ? raw : null;
}

/** Pages a builder's customer reads. Not our user, not our analytics. */
const BUYER_FACING = /^\/(?:quote|register)\//;

let enabled = false;
let scriptRequested = false;

export function isAnalyticsEnabled(): boolean {
  return enabled;
}

/* ─────────────────────────── paths ─────────────────────────── */

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * A segment that is an identifier rather than a word: a uuid, a number, or a
 * long token with digits in it. The named routes below catch the known ones;
 * this is what stops a route added later from leaking one.
 */
function looksLikeId(segment: string): boolean {
  if (UUID.test(segment)) return true;
  if (/^\d+$/.test(segment)) return true;
  return segment.length >= 16 && /\d/.test(segment) && /^[A-Za-z0-9_-]+$/.test(segment);
}

/**
 * Routes whose parameter is named outright, because ANY value there is an id
 * or a secret — well-formed or not. The quote token and the roadmap token are
 * each the whole key to an unauthenticated page.
 */
const NAMED: Array<[RegExp, string]> = [
  [/^\/quote\/[^/]+/, "/quote/:token"],
  [/^\/worksheet-response\/[^/]+/, "/worksheet-response/:id"],
  [/^\/sign\/[^/]+/, "/sign/:publicId"],
  [/^\/register\/[^/]+/, "/register/:projectId"],
  [/^\/roadmap\/[^/]+/, "/roadmap/:token"],
  [/^\/projects\/[^/]+\/inventory\/submit-worksheet\/[^/]+/, "/projects/:projectId/inventory/submit-worksheet/:unitId"],
  [/^\/projects\/(?!undefined(?:\/|$))[^/]+/, "/projects/:projectId"],
  [/^\/admin\/billing\/(edit|quotes|template)\/[^/]+/, "/admin/billing/$1/:id"],
];

/**
 * The path as analytics may see it: no query string, no hash, and every id
 * replaced by the name of the route parameter it fills.
 */
export function analyticsPath(rawPath: string): string {
  let path = rawPath.split(/[?#]/)[0] || "/";
  if (!path.startsWith("/")) path = `/${path}`;
  for (const [pattern, replacement] of NAMED) {
    if (pattern.test(path)) {
      path = path.replace(pattern, replacement);
      break;
    }
  }
  return path
    .split("/")
    .map((segment) => (segment.startsWith(":") || !looksLikeId(segment) ? segment : ":id"))
    .join("/");
}

/* ─────────────────────────── titles ─────────────────────────── */

/**
 * The name of each project section, keyed by the URL segment after
 * `/projects/:projectId/`. The labels match the sidebar (shared/navigationMenu.ts)
 * — tests/analytics.test.ts holds every project section there to a title here.
 */
const PROJECT_SECTIONS: Record<string, string> = {
  dashboard: "Dashboard",
  contacts: "Contacts",
  tasks: "Tasks",
  campaigns: "Campaigns",
  "email-marketing": "Email Marketing",
  "website-analytics": "Website Analytics",
  offers: "Offers",
  "sales-list": "Sales List",
  signing: "Signing",
  availability: "Availability",
  reports: "Reports",
  deposits: "Deposits",
  settings: "Project Settings",
  showroom: "Showroom",
  "agent-portal": "Agent Portal",
  "availability/qmis": "Quick Move-In",
  "availability/outside-listings": "Outside Listings",
  "availability/pricing": "Pricing",
  "availability/siting": "Siting",
  "inventory/setup": "Inventory Setup",
  "integrations/salefish": "SaleFish Integration",
};

const EXACT: Record<string, string> = {
  "/": PRODUCT,
  "/login": "Sign In",
  "/forgot-password": "Forgot Password",
  "/reset-password": "Reset Password",
  "/privacy-policy": "Privacy Policy",
  "/terms-of-use": "Terms of Use",
  "/home": "Home",
  "/projects": "Projects",
  "/account/security/passkeys": "Passkeys",
  "/admin": "Platform Settings",
  "/admin/settings": "Platform Settings",
  "/admin/users": "Users",
  "/admin/roles": "Roles",
  "/admin/companies": "Companies",
  "/admin/projects": "Projects",
  "/admin/integrations": "Integrations",
  "/admin/translations": "Translations",
  "/company-admin": "Company Details",
  "/company-admin/users": "Team Members",
  "/company-admin/features": "Features & Billing",
  "/company-admin/billing": "Billing",
};

const PREFIXES: Array<[string, string]> = [
  ["/quote/", "Your Quote"],
  ["/register/", "Register"],
  ["/roadmap/", "Roadmap"],
  ["/admin/usage", "Usage"],
  ["/admin/billing", "Billing & Proposals"],
  ["/admin/competitor-analysis", "Competitor Analysis"],
  // Each of these left the browser tab reading just "SaleFish" (found testing, 2026-10-23).
  ["/admin/reports", "Reports"],
  ["/admin/user-log", "User Log"],
  ["/admin/roadmap", "Roadmap"],
  ["/admin/documents", "Document Library"],
  ["/company-admin/", "Company Admin"],
];

/** A short, id-free name for the page at `rawPath`. */
export function titleForPath(rawPath: string): string {
  const path = (rawPath.split(/[?#]/)[0] || "/").replace(/\/+$/, "") || "/";
  if (EXACT[path]) return EXACT[path];

  const project = /^\/projects\/[^/]+(?:\/(.*))?$/.exec(path);
  if (project && !path.startsWith("/projects/undefined")) {
    const rest = project[1] ?? "";
    if (!rest) return "Dashboard"; // /projects/:projectId renders the dashboard
    for (const [section, title] of Object.entries(PROJECT_SECTIONS).sort((a, b) => b[0].length - a[0].length)) {
      if (rest === section || rest.startsWith(`${section}/`)) return title;
    }
    return PRODUCT;
  }

  for (const [prefix, title] of PREFIXES) {
    if (path === prefix.replace(/\/$/, "") || path.startsWith(prefix)) return title;
  }
  return PRODUCT;
}

/*
 * The tab's words in the reader's language. `titleForPath` stays English on
 * purpose — it is also the `page_title` analytics receives, and reports group
 * by it — so the translation is applied only where the title is SHOWN.
 */
const TITLE_MSGS: Record<string, Msg> = {
  "Availability": msg("shellTitleAvailability", "Availability"),
  "Availability Matrix": msg("shellTitleAvailabilityMatrix", "Availability Matrix"),
  "Billing": msg("shellTitleBilling", "Billing"),
  "Billing & Proposals": msg("shellTitleBillingProposals", "Billing & Proposals"),
  "Campaigns": msg("shellTitleCampaigns", "Campaigns"),
  "Companies": msg("shellTitleCompanies", "Companies"),
  "Company Admin": msg("shellTitleCompanyAdmin", "Company Admin"),
  "Company Details": msg("shellTitleCompanyDetails", "Company Details"),
  "Competitor Analysis": msg("shellTitleCompetitorAnalysis", "Competitor Analysis"),
  "Contacts": msg("shellTitleContacts", "Contacts"),
  "Dashboard": msg("shellTitleDashboard", "Dashboard"),
  "Email Marketing": msg("shellTitleEmailMarketing", "Email Marketing"),
  "Features & Billing": msg("shellTitleFeaturesBilling", "Features & Billing"),
  "Forgot Password": msg("shellTitleForgotPassword", "Forgot Password"),
  "Home": msg("shellTitleHome", "Home"),
  "Integrations": msg("shellTitleIntegrations", "Integrations"),
  "Inventory Setup": msg("shellTitleInventorySetup", "Inventory Setup"),
  "Offers": msg("shellTitleOffers", "Offers"),
  "Passkeys": msg("shellTitlePasskeys", "Passkeys"),
  "Platform Settings": msg("shellTitlePlatformSettings", "Platform Settings"),
  "Privacy Policy": msg("shellTitlePrivacyPolicy", "Privacy Policy"),
  "Project Settings": msg("shellTitleProjectSettings", "Project Settings"),
  "Projects": msg("shellTitleProjects", "Projects"),
  "Register": msg("shellTitleRegister", "Register"),
  "Reports": msg("shellTitleReports", "Reports"),
  "Reset Password": msg("shellTitleResetPassword", "Reset Password"),
  "Roadmap": msg("shellTitleRoadmap", "Roadmap"),
  "Roles": msg("shellTitleRoles", "Roles"),
  "SaleFish Integration": msg("shellTitleSaleFishIntegration", "SaleFish integration"),
  "Sales List": msg("shellTitleSalesList", "Sales List"),
  "Deposits": msg("shellTitleDeposits", "Deposits"),
  "Sign In": msg("shellTitleSignIn", "Sign In"),
  "Signing": msg("shellTitleSigning", "Signing"),
  "Tasks": msg("shellTitleTasks", "Tasks"),
  "Team Members": msg("shellTitleTeamMembers", "Team Members"),
  "Terms of Use": msg("shellTitleTermsOfUse", "Terms of Use"),
  "Translations": msg("shellTitleTranslations", "Translations"),
  "Usage": msg("shellTitleUsage", "Usage"),
  "Users": msg("shellTitleUsers", "Users"),
  "Your Quote": msg("shellTitleYourQuote", "Your Quote"),
  "Pricing": msg("shellTitlePricing", "Pricing"),
  "Siting": msg("shellTitleSiting", "Siting"),
  "Showroom": msg("shellTitleShowroom", "Showroom"),
  "Agent Portal": msg("shellTitleAgentPortal", "Agent Portal"),
  "Quick Move-In": msg("shellTitleQuickMoveIn", "Quick Move-in"),
  "Outside Listings": msg("shellTitleOutsideListings", "Outside Listings"),
  "Document Library": msg("shellTitleDocumentLibrary", "Document Library"),
  "User Log": msg("shellTitleUserLog", "User Log"),
};

/** The page title in the reader's language, or as given when it has none. */
export function localizedPageTitle(title: string, t?: (m: Msg) => string): string {
  const m = TITLE_MSGS[title];
  return m && t ? t(m) : title;
}

/** The tab title for a page: its name, then the product — never the product twice. */
export function documentTitleFor(pageTitle: string): string {
  return pageTitle === PRODUCT ? PRODUCT : `${pageTitle} · ${PRODUCT}`;
}

/* ─────────────────────────── start-up ─────────────────────────── */

/**
 * Configure GA, and fetch its script once the app has started.
 *
 * Call once from main.tsx, before or after the first render — the fetch waits
 * for `load` and then idle time either way. Idempotent. Returns whether
 * analytics is on for this page.
 */
export function initAnalytics(options?: { id?: string | null; location?: Location; defer?: (run: () => void) => void }): boolean {
  if (enabled || typeof window === "undefined") return enabled;
  const id = options && "id" in options ? options.id ?? null : configuredId();
  if (!id || !MEASUREMENT_ID_SHAPE.test(id)) return false;

  const where = options?.location ?? window.location;
  if (BUYER_FACING.test(where.pathname)) return false;

  window.dataLayer = window.dataLayer || [];
  // A classic function, so gtag receives its raw `arguments` exactly as
  // Google's own snippet does.
  function gtag() {
    // eslint-disable-next-line prefer-rest-params
    window.dataLayer!.push(arguments);
  }
  window.gtag = gtag as unknown as (...args: unknown[]) => void;

  const safePath = analyticsPath(where.pathname);
  window.gtag("js", new Date());
  window.gtag("config", id, {
    // SPA: one page_view per route change, sent by trackPageview.
    send_page_view: false,
    // gtag otherwise reads document.location for every event, which carries
    // the ids analyticsPath exists to remove.
    page_location: where.origin + safePath,
    page_path: safePath,
    anonymize_ip: true,
    allow_google_signals: false,
    allow_ad_personalization_signals: false,
  });

  enabled = true;

  const inject = () => {
    if (scriptRequested) return;
    scriptRequested = true;
    const script = document.createElement("script");
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(id)}`;
    document.head.appendChild(script);
  };
  (options?.defer ?? afterStart)(inject);
  return true;
}

/** After `load`, then in idle time: never in front of first paint. */
function afterStart(run: () => void): void {
  const idle = () => {
    const ric = (window as Window & { requestIdleCallback?: (cb: () => void, o?: { timeout: number }) => number })
      .requestIdleCallback;
    if (ric) ric(run, { timeout: 4000 });
    else setTimeout(run, 1500);
  };
  if (document.readyState === "complete") idle();
  else window.addEventListener("load", idle, { once: true });
}

/* ─────────────────────────── page views ─────────────────────────── */

let lastSentPath: string | null = null;
/** The `t` the tab was last titled with, so a view's title can be withdrawn in the reader's language. */
let lastT: ((m: Msg) => string) | undefined;
/**
 * A page whose views live behind a query (Deposits' `?section=commissions`,
 * `?section=proppay`) names the selected view in the tab — the path alone
 * said "Deposits" over the Commissions list (audit, 2026-10-27). Keyed by
 * path, like the breadcrumb's published title, so it never outlives its page.
 * Analytics' page_title stays the path's: a view is the same page there.
 */
let viewTitle: { path: string; title: string } | null = null;

/**
 * Title the tab, and send one page_view for the page — with its ids removed.
 * The title is set whether or not analytics is on.
 */
export function trackPageview(rawPath: string, t?: (m: Msg) => string): void {
  const title = titleForPath(rawPath);
  lastT = t;
  const pathname = rawPath.split(/[?#]/)[0] || "/";
  const shown = viewTitle?.path === pathname ? viewTitle.title : localizedPageTitle(title, t);
  if (typeof document !== "undefined") document.title = documentTitleFor(shown);
  if (!enabled || !window.gtag) return;

  const path = analyticsPath(rawPath);
  if (path === lastSentPath) return;
  lastSentPath = path;
  const location = window.location.origin + path;
  // Also the default for the events that follow on this page, which would
  // otherwise carry the real URL.
  window.gtag("set", { page_path: path, page_location: location });
  window.gtag("event", "page_view", { page_path: path, page_title: title, page_location: location });
}

/**
 * Title the tab and record the page view on every PATH change (not on a
 * `?tab=` change — that is the same page). Call once, where the real location
 * is visible to every branch: AppContent.
 */
export function usePageViews(location: string, t?: (m: Msg) => string): void {
  const pathname = location.split(/[?#]/)[0] || "/";
  useEffect(() => {
    // `t` changes when the language (or its translations) arrive: the tab is
    // retitled, and the page_view is not resent (lastSentPath).
    trackPageview(pathname, t);
  }, [pathname, t]);
}

/**
 * Name the open view in the tab: `useViewDocumentTitle(t("dpCommissions", "Commissions"))`.
 * `null` leaves the path's own title. A layout effect, so it is in place before
 * AppContent's page-view effect titles the tab on the first render.
 */
export function useViewDocumentTitle(title: string | null): void {
  useLayoutEffect(() => {
    if (!title || typeof window === "undefined") return;
    const path = window.location.pathname;
    viewTitle = { path, title };
    document.title = documentTitleFor(title);
    return () => {
      if (viewTitle?.path === path && viewTitle.title === title) viewTitle = null;
      if (window.location.pathname === path) document.title = documentTitleFor(localizedPageTitle(titleForPath(path), lastT));
    };
  }, [title]);
}

/* ─────────────────────────── identity ─────────────────────────── */

async function sha256Hex(input: string): Promise<string> {
  const data = new TextEncoder().encode(input);
  const digest = await crypto.subtle.digest("SHA-256", data);
  return Array.from(new Uint8Array(digest))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
}

/** The pseudonymous id analytics sees: SHA-256 of the trimmed, lower-cased email. */
export function hashedUserId(email: string): Promise<string> {
  return sha256Hex(email.trim().toLowerCase());
}

let lastIdentity: string | null = null;

export interface AnalyticsUser {
  email?: string | null;
  role?: string | null;
  isCompanyAdmin?: boolean | null;
}

/**
 * Say who is signed in — as a hash, with two coarse, non-identifying
 * properties — or that nobody is. Never the email, never the database id,
 * never a company or project id.
 */
export async function identifyUser(user: AnalyticsUser | null | undefined): Promise<void> {
  if (!enabled || !window.gtag) return;
  if (!user?.email) {
    if (lastIdentity !== null) {
      lastIdentity = null;
      window.gtag("set", { user_id: null });
    }
    return;
  }
  window.gtag("set", "user_properties", {
    user_role: user.role ?? null,
    is_company_admin: !!user.isCompanyAdmin,
  });
  try {
    const id = await hashedUserId(user.email);
    if (id === lastIdentity) return;
    lastIdentity = id;
    window.gtag("set", { user_id: id });
  } catch {
    /* crypto.subtle is unavailable outside a secure context — events still flow, unattributed */
  }
}

/** Keep the analytics identity in step with the signed-in user. */
export function useAnalyticsIdentity(user: AnalyticsUser | null | undefined): void {
  const email = user?.email ?? null;
  const role = user?.role ?? null;
  const admin = !!user?.isCompanyAdmin;
  useEffect(() => {
    void identifyUser(email ? { email, role, isCompanyAdmin: admin } : null);
  }, [email, role, admin]);
}

/** Test seam. Never called by the app. */
export function __resetAnalyticsForTests(): void {
  enabled = false;
  scriptRequested = false;
  lastSentPath = null;
  lastIdentity = null;
}
